// admin/src/pages/Programs.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import TopNav from '../components/TopNav';
import ProgramCardPro from '../components/ProgramCardPro';
import ProgramEditModal from '../components/ProgramEditModal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useAdminStore } from '../context/adminStore';
import { useAdminAuth } from '../auth/AdminAuthContext';

const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Programs() {
  const { state, api } = useAdminStore();
  const { programs, filterText, loading, error } = state;
  const { secret } = useAdminAuth();
  const [localFilter, setLocalFilter] = useState(filterText);

  // Modal states
  const [editingProgram, setEditingProgram] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deletingProgram, setDeletingProgram] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // NEW: Degree level states
  const [degreeLevelTags, setDegreeLevelTags] = useState([]);
  const [loadingDegreeTags, setLoadingDegreeTags] = useState(true);
  const [selectedDegreeLevels, setSelectedDegreeLevels] = useState([]);

  // StrictMode double-mount guard
  const didFetchRef = useRef(false);
  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    api.loadPrograms();
  }, [api]);

  // NEW: Fetch degree level tags
  useEffect(() => {
    const fetchDegreeLevels = async () => {
      try {
        const res = await fetch(
          `${API_ROOT}/api/tags/degree-levels?tenantId=${encodeURIComponent(state.tenantId)}`,
          {
            headers: {
              'x-admin-secret': secret || ''
            }
          }
        );
        if (res.ok) {
          const data = await res.json();
          setDegreeLevelTags(data.tags || []);
        }
      } catch (err) {
        console.error('Error fetching degree levels:', err);
      } finally {
        setLoadingDegreeTags(false);
      }
    };

    if (state.tenantId && secret) {
      fetchDegreeLevels();
    }
  }, [state.tenantId, secret]);

  // Filter programs by text and degree level
  const filtered = useMemo(() => {
    let result = programs;

    // Text filter
    if (localFilter) {
      const q = localFilter.toLowerCase();
      result = result.filter(p =>
        (p.title || '').toLowerCase().includes(q) ||
        (p.permalink || '').toLowerCase().includes(q)
      );
    }

    // Degree level filter
    if (selectedDegreeLevels.length > 0) {
      result = result.filter(p => {
        if (!p.degreeLevelTags || p.degreeLevelTags.length === 0) return false;
        return p.degreeLevelTags.some(tag => selectedDegreeLevels.includes(tag));
      });
    }

    return result;
  }, [programs, localFilter, selectedDegreeLevels]);

  // NEW: Calculate degree level stats
  const degreeLevelStats = useMemo(() => {
    const stats = degreeLevelTags.map(tag => ({
      slug: tag.slug,
      name: tag.name,
      count: programs.filter(p => 
        p.degreeLevelTags && p.degreeLevelTags.includes(tag.slug)
      ).length
    }));

    const untagged = programs.filter(p => 
      !p.degreeLevelTags || p.degreeLevelTags.length === 0
    ).length;

    return { stats, untagged };
  }, [programs, degreeLevelTags]);

  // Handlers
  const handleNew = () => {
    setEditingProgram(null);
    setShowEditModal(true);
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditingProgram(null);
  };

  const handleSave = async (programData) => {
    await api.saveProgram(programData);
    // Refresh degree level stats after save
    api.loadPrograms();
  };

  const handleDeleteClick = (program) => {
    setDeletingProgram(program);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingProgram) return;
    try {
      await api.deleteProgram(deletingProgram._id);
      setShowDeleteConfirm(false);
      setDeletingProgram(null);
    } catch (err) {
      alert(`Failed to delete: ${err.message}`);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletingProgram(null);
  };

  // NEW: Toggle degree level filter
  const toggleDegreeFilter = (slug) => {
    setSelectedDegreeLevels(prev => {
      if (prev.includes(slug)) {
        return prev.filter(s => s !== slug);
      } else {
        return [...prev, slug];
      }
    });
  };

  return (
    <>
      <TopNav />
      <div className="page-pad">
        <header className="adm-card__header" style={{ border: 'none', padding: 0, marginBottom: 10 }}>
          <h1 style={{ margin: 0 }}>Programs</h1>
        </header>

        {/* NEW: Degree Level Stats Dashboard */}
        {!loadingDegreeTags && degreeLevelTags.length > 0 && (
          <div style={{ 
            padding: 20,
            background: '#f9fafb',
            borderRadius: 8,
            marginBottom: 20,
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 600 }}>
              Degree Level Distribution
            </h3>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 16
            }}>
              {degreeLevelStats.stats.map(stat => (
                <div key={stat.slug}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>
                    {stat.name}
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#111827' }}>
                    {stat.count}
                  </div>
                </div>
              ))}
              {degreeLevelStats.untagged > 0 && (
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#ef4444', marginBottom: 4 }}>
                    Untagged
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#ef4444' }}>
                    {degreeLevelStats.untagged}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="admin-toolbar" style={{ marginBottom: 16 }}>
          <input
            className="input"
            placeholder="Filter by title or link…"
            value={localFilter}
            onChange={e => setLocalFilter(e.target.value)}
            style={{ flex: 1, maxWidth: 400 }}
          />
          <button className="btn" onClick={() => api.setFilter(localFilter)}>Apply</button>
          <button
            className="btn btn--primary"
            onClick={handleNew}
          >
            + New Program
          </button>
        </div>

        {/* NEW: Degree Level Filter */}
        {!loadingDegreeTags && degreeLevelTags.length > 0 && (
          <div style={{ 
            display: 'flex', 
            gap: 8, 
            flexWrap: 'wrap',
            marginBottom: 20,
            padding: 16,
            background: 'white',
            borderRadius: 8,
            border: '1px solid #e5e7eb'
          }}>
            <span style={{ 
              fontSize: '0.85rem', 
              color: '#6b7280', 
              alignSelf: 'center',
              fontWeight: 500
            }}>
              Filter by degree:
            </span>
            {degreeLevelTags.map(tag => (
              <button
                key={tag.slug}
                onClick={() => toggleDegreeFilter(tag.slug)}
                className={selectedDegreeLevels.includes(tag.slug) ? 'btn btn--primary' : 'btn'}
                style={{ 
                  fontSize: '0.8rem', 
                  padding: '6px 14px',
                  minHeight: 'auto'
                }}
                title={tag.description}
              >
                {tag.name}
                {selectedDegreeLevels.includes(tag.slug) && ' ✓'}
                <span style={{ 
                  marginLeft: 6,
                  fontSize: '0.75rem',
                  opacity: 0.7
                }}>
                  ({tag.count})
                </span>
              </button>
            ))}
            {selectedDegreeLevels.length > 0 && (
              <button
                onClick={() => setSelectedDegreeLevels([])}
                className="btn"
                style={{ 
                  fontSize: '0.8rem', 
                  padding: '6px 14px',
                  minHeight: 'auto',
                  color: '#ef4444',
                  fontWeight: 500
                }}
              >
                Clear Filter
              </button>
            )}
          </div>
        )}

        {loading && <div className="muted">Loading…</div>}
        {error && <div className="error">{error}</div>}

        {/* Filter status message */}
        {!loading && selectedDegreeLevels.length > 0 && (
          <div style={{ 
            marginBottom: 16,
            fontSize: '0.875rem',
            color: '#6b7280'
          }}>
            Showing {filtered.length} of {programs.length} programs
          </div>
        )}

        <div className="admin-grid">
          {filtered.map(p => (
            <ProgramCardPro
              key={p._id}
              program={p}
              degreeLevelTags={degreeLevelTags}
              onEdit={() => handleEdit(p)}
              onDelete={() => handleDeleteClick(p)}
            />
          ))}
        </div>

        {!loading && filtered.length === 0 && (
          <div className="adm-empty" style={{ textAlign: 'center', padding: 40 }}>
            {localFilter || selectedDegreeLevels.length > 0
              ? 'No programs match your filters'
              : 'No programs yet'}
          </div>
        )}
      </div>

      <ProgramEditModal
        program={editingProgram}
        isOpen={showEditModal}
        onClose={handleCloseEdit}
        onSave={handleSave}
        tenantId={state.tenantId}
        adminSecret={secret}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Program"
        message={`Are you sure you want to delete "${deletingProgram?.title}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="Delete"
        danger={true}
      />
    </>
  );
}